import type { IconKind } from '@/components/AppIcon';

/**
 * The module and screen map, carried over from the design artboard.
 *
 * Each screen names the permission that reveals it, so the sidebar is built
 * from what the user can actually do rather than from their role name. A custom
 * role created in the admin screens therefore gets a correct sidebar with no
 * code change.
 *
 * `built: false` marks a screen that exists in the map but has no
 * implementation yet — shown, tagged "Coming soon", and honest about it. A
 * module with no built screen at all is tagged the same way in the sidebar.
 *
 * There is no Reports module: each module prints its own reports, and data
 * is never exported as a file.
 */

export interface ScreenDef {
  id: string;
  label: string;
  labelUr: string;
  permission: string;
  /**
   * A screen that merges what used to be several now needs several
   * permissions: it is shown when the caller holds `permission` or any of
   * these. Each section inside it is still gated on its own.
   */
  alsoVisibleWith?: string[];
  built?: boolean;
  /**
   * Set on a screen already folded into a merged one (e.g. Reference) that
   * has not been removed yet — the destination shown as its tag and its
   * tooltip, so people see it coming before the tab actually disappears.
   *
   * Mutually exclusive with `built: false` as far as the reader is concerned: a
   * tab cannot be both arriving and leaving, so the sidebar shows this tag
   * instead of "Coming soon", never as well as it.
   */
  removingSoon?: string;
  /** Shown before the tab's name. */
  icon?: IconKind;
}

/**
 * Screens that were folded into a merged one, and the node within it they
 * land on. Bookmarks, "open in new window" links and the top-bar search all
 * carry the old ids, so they keep working rather than dead-ending.
 */
export const LEGACY_SCREENS: Record<string, { screen: string; node?: string }> = {
  'inv-dash': { screen: 'inv-workbench' },
  'inv-stock': { screen: 'inv-workbench' },
  'inv-trace': { screen: 'inv-workbench', node: 'lots' },
  'inv-sku': { screen: 'inv-reference', node: 'items' },
  'mdm-store': { screen: 'inv-reference', node: 'stores' },
  'mdm-supplier': { screen: 'inv-reference', node: 'suppliers' },
  // Sheds, sections and pens are now reached through the Farm Manager tree.
  'mdm-shed': { screen: 'mdm-farm' },
  // Feed Reconciliation's job — feed issued against feed used, and the gap
  // between them — is now the feed section of a flock's summary, beside the
  // bird numbers that explain it.
  'ops-feed': { screen: 'mdm-flock' },
  // The weighing is part of the dispatch document now, so the old tab lands on
  // the weights rather than dead-ending.
  'sd-weigh': { screen: 'sd-gate', node: 'weights' },
  // Both broker screens are drawn from the journal on one Reports & Ledgers tab.
  'fin-broker': { screen: 'fin-rep' },
  'fin-stmt': { screen: 'fin-rep' },
};

/**
 * The one tag a screen's tab carries, or none.
 *
 * One function returning one answer, because the two states are mutually
 * exclusive and rendering them as two independent conditions is how Broker
 * Ledgers ended up labelled "Coming soon" and "Removing soon" at the same time.
 *
 * "Removing soon" wins over "Coming soon". A tab on its way out is the more
 * useful thing to know, and whether it was ever built does not change what a
 * person should do about it — which is go to the screen it is moving to.
 */
export function screenTag(
  screen: ScreenDef,
): { kind: 'removing'; label: string; title: string } | { kind: 'coming'; label: string } | null {
  if (screen.removingSoon) {
    return {
      kind: 'removing',
      label: 'Removing soon',
      title: `Moving to ${screenLabel(screen.removingSoon)}`,
    };
  }
  if (!screen.built) return { kind: 'coming', label: 'Coming soon' };
  return null;
}

/** One screen's label, for a message that has to name where a tab is going. */
export function screenLabel(id: string): string {
  for (const m of MODULES) {
    const found = m.screens.find((s) => s.id === id);
    if (found) return found.label;
  }
  return id;
}

export function resolveScreenId(id: string): string {
  return LEGACY_SCREENS[id]?.screen ?? id;
}

export interface ModuleDef {
  id: string;
  label: string;
  labelUr: string;
  screens: ScreenDef[];
}

export const MODULES: ModuleDef[] = [
  {
    id: 'home',
    label: 'Dashboard',
    labelUr: 'ڈیش بورڈ',
    screens: [
      // Reachable by anyone signed in: it shows only what the viewer's own
      // permissions allow, so it needs no permission of its own.
      { id: 'home', label: 'Dashboard', labelUr: 'ڈیش بورڈ', permission: '*', built: true },
      { id: 'home-announce', label: 'Announcements', labelUr: 'اعلانات', permission: 'com.announcement.read', built: true },
      { id: 'home-calendar', label: 'Calendar', labelUr: 'کیلنڈر', permission: '*', built: true },
      // Work saved on this PC that the server refused, or has not had yet
      // (.docs/023). Everybody may have some, so it needs no permission.
      { id: 'home-stuck', label: 'Offline work', labelUr: 'آف لائن کام', permission: '*', built: true },
      // Anyone asking for a posted record in any module to be put right, and
      // whoever reviews it: shared across modules like Announcements.
      { id: 'home-corr', label: 'Correction Requests', labelUr: 'درستی کی درخواستیں', permission: 'com.correction.read' },
    ],
  },
  {
    id: 'inv',
    label: 'Inventory & Stores',
    labelUr: 'انوینٹری اور اسٹور',
    screens: [
      { id: 'inv-workbench', label: 'Stock', labelUr: 'اسٹاک', permission: 'inv.dashboard.read', alsoVisibleWith: ['inv.batch.read', 'inv.ledger.read'], built: true, icon: 'stock' },
      { id: 'inv-reference', label: 'Items Manager', labelUr: 'آئٹم مینیجر', permission: 'inv.sku.read', alsoVisibleWith: ['inv.store.read', 'inv.supplier.read'], built: true, icon: 'catalog' },
      { id: 'inv-inward', label: 'Stock IN', labelUr: 'اسٹاک ان', permission: 'inv.inward.read', built: true, icon: 'inbound' },
      { id: 'inv-consume', label: 'Stock OUT', labelUr: 'اسٹاک آؤٹ', permission: 'inv.consumption.read', built: true, icon: 'outbound' },
      { id: 'inv-xfer', label: 'Transfers & Returns', labelUr: 'منتقلی اور واپسی', permission: 'inv.transfer.read', built: true, icon: 'transfer' },
      { id: 'inv-adjust', label: 'Stock Adjustments', labelUr: 'اسٹاک ترمیم', permission: 'inv.adjustment.read', built: true, icon: 'adjust' },
      { id: 'inv-demand', label: 'Stock Demands', labelUr: 'اسٹاک ڈیمانڈ', permission: 'inv.demand.read', built: true, icon: 'demand' },
    ],
  },
  {
    id: 'mdm',
    label: 'Farm Manager',
    labelUr: 'فارم مینیجر',
    screens: [
      { id: 'mdm-dash', label: 'Dashboard', labelUr: 'ڈیش بورڈ', permission: 'mdm.farm.read', alsoVisibleWith: ['mdm.shed.read'], built: true, icon: 'dashboard' },
      { id: 'mdm-farm', label: 'Farms & Sheds', labelUr: 'فارم اور شیڈ', permission: 'mdm.farm.read', alsoVisibleWith: ['mdm.shed.read'], built: true, icon: 'farm' },
      // "Flocks" rather than "Flocks & Grading" until grading is built: a tab
      // naming something it does not do is a promise the screen breaks.
      { id: 'mdm-flock', label: 'Flocks', labelUr: 'فلاک', permission: 'mdm.flock.read', built: true, icon: 'flock' },
    ],
  },
  {
    id: 'ops',
    label: 'Daily Operations',
    labelUr: 'روزمرہ آپریشنز',
    screens: [
      // One screen for a shed's day — dead, culled, feed and weight — because
      // that is one line on the paper register it is copied from. It replaced
      // the separate Mortality Tracker and Feed Reconciliation tabs.
      { id: 'ops-mort', label: 'Daily Register', labelUr: 'روزانہ رجسٹر', permission: 'ops.daily.read', built: true, icon: 'attendance' },
      { id: 'ops-med', label: 'Medical & Chemical Log', labelUr: 'دوا اور کیمیکل', permission: 'ops.medical.read' },
    ],
  },
  {
    id: 'sd',
    label: 'Sales & Dispatch',
    labelUr: 'فروخت اور ترسیل',
    screens: [
      // One screen for the gate pass and its weighing, because that is one row on
      // the spreadsheet it replaces — the same argument the Daily Register won.
      // `alsoVisibleWith` is what lets the weighbridge operator reach it.
      { id: 'sd-gate', label: 'Dispatch', labelUr: 'ترسیل', permission: 'sd.gatepass.read', alsoVisibleWith: ['sd.weighbridge.read'], built: true, icon: 'outbound' },
      { id: 'sd-weigh', label: 'Weighbridge & Weights', labelUr: 'وزن کانٹا', permission: 'sd.weighbridge.read', removingSoon: 'sd-gate' },
      // Bags, manure and scrap on one screen: the same paperwork with a different
      // load on it, so the tab no longer names only one of the three.
      { id: 'sd-bags', label: 'Other Sales', labelUr: 'دیگر فروخت', permission: 'sd.emptybag.read', built: true, icon: 'stock' },
    ],
  },
  {
    id: 'fin',
    label: 'Financials & Ledgers',
    labelUr: 'مالیات اور کھاتے',
    screens: [
      { id: 'fin-dash', label: 'Finance Dashboard', labelUr: 'مالیات ڈیش بورڈ', permission: 'fin.report.read', built: true, icon: 'dashboard' },
      { id: 'fin-bill', label: 'Dispatch Billing', labelUr: 'ترسیل بلنگ', permission: 'fin.bill.read', built: true, icon: 'cash' },
      { id: 'fin-recv', label: 'Receipts', labelUr: 'وصولیاں', permission: 'fin.receipt.read', built: true, icon: 'paid' },
      // Was "Petty Cash (Khata)". It is the whole cash book now — a farm's cash is
      // not petty — and the permission code stays the same so no grant has to move.
      { id: 'fin-cash', label: 'Cash Book (Khata)', labelUr: 'روزمرہ کھاتہ', permission: 'fin.pettycash.read', built: true, icon: 'cash' },
      { id: 'fin-exp', label: 'Farm Expenses', labelUr: 'فارم اخراجات', permission: 'fin.expense.read', built: true, icon: 'advance' },
      // Was "Broker Ledgers", and it draws any account or party now. The reports,
      // the ledger pages and the statements are one screen with a picker, because
      // the dates and the farm are the same questions for all of them.
      { id: 'fin-rep', label: 'Reports & Ledgers', labelUr: 'رپورٹس اور کھاتے', permission: 'fin.report.read', alsoVisibleWith: ['fin.ledger.read', 'fin.statement.read', 'fin.margin.read'], built: true, icon: 'report' },
      { id: 'fin-jv', label: 'Journal Vouchers', labelUr: 'جرنل واؤچر', permission: 'fin.journal.read', built: true, icon: 'calculate' },
      { id: 'fin-setup', label: 'Finance Setup', labelUr: 'مالیات ترتیبات', permission: 'fin.account.read', alsoVisibleWith: ['fin.bank.read', 'fin.period.read', 'fin.broker.read'], built: true, icon: 'setup' },
      // Folded into Reports & Ledgers, which draws the same statement from the
      // journal rather than from a stored running balance.
      { id: 'fin-broker', label: 'Broker Ledgers', labelUr: 'بروکر کھاتے', permission: 'fin.ledger.read', removingSoon: 'fin-rep' },
      { id: 'fin-stmt', label: 'Broker Statements', labelUr: 'بروکر گوشوارے', permission: 'fin.statement.read', removingSoon: 'fin-rep' },
    ],
  },
  {
    id: 'hr',
    label: 'HR & Payroll',
    labelUr: 'عملہ اور تنخواہ',
    screens: [
      { id: 'hr-dash', label: 'HR Dashboard', labelUr: 'عملہ ڈیش بورڈ', permission: 'hr.dashboard.read', built: true, icon: 'dashboard' },
      { id: 'hr-emp', label: 'Employees', labelUr: 'ملازمین', permission: 'hr.employee.read', built: true, icon: 'people' },
      { id: 'hr-att', label: 'Attendance', labelUr: 'حاضری', permission: 'hr.attendance.read', built: true, icon: 'attendance' },
      { id: 'hr-leave', label: 'Leave', labelUr: 'چھٹیاں', permission: 'hr.leave.read', built: true, icon: 'leave' },
      { id: 'hr-adv', label: 'Advances & Loans', labelUr: 'ایڈوانس اور قرض', permission: 'hr.advance.read', built: true, icon: 'advance' },
      { id: 'hr-pay', label: 'Payroll & Slips', labelUr: 'تنخواہ اور سلپ', permission: 'hr.payroll.read', built: true, icon: 'payroll' },
      { id: 'hr-settle', label: 'Final Settlements', labelUr: 'حتمی تصفیہ', permission: 'hr.settlement.read', built: true, icon: 'settlement' },
      { id: 'hr-rep', label: 'HR Reports', labelUr: 'عملہ رپورٹس', permission: 'hr.report.read', built: true, icon: 'report' },
      { id: 'hr-setup', label: 'HR Setup', labelUr: 'عملہ ترتیبات', permission: 'hr.setup.read', built: true, icon: 'setup' },
      { id: 'hr-mypay', label: 'My Pay', labelUr: 'میری تنخواہ', permission: 'hr.self.read', built: true, icon: 'payslip' },
    ],
  },
  {
    id: 'iam',
    label: 'Identity & Access',
    labelUr: 'رسائی اور اجازتیں',
    screens: [
      { id: 'iam-users', label: 'Users', labelUr: 'صارفین', permission: 'iam.user.read', built: true, icon: 'people' },
      { id: 'iam-roles', label: 'Roles & Permissions', labelUr: 'کردار اور اجازتیں', permission: 'iam.role.read', built: true, icon: 'locked' },
      { id: 'iam-helpers', label: 'Access Helpers', labelUr: 'رسائی معاونین', permission: 'iam.helper.read', built: true, icon: 'people' },
      // Not a permission: shown to whoever the Managing Director has made an
      // access helper, and to nobody else (.docs/027).
      { id: 'iam-mypeople', label: 'My People', labelUr: 'میرے لوگ', permission: '@helper', built: true, icon: 'people' },
      { id: 'iam-audit', label: 'Audit Log', labelUr: 'آڈٹ لاگ', permission: 'iam.audit.read', built: true, icon: 'history' },
    ],
  },
];

/**
 * Whether a screen is visible.
 *
 * '*' means any signed-in user, used by the dashboard: it renders only the
 * parts the viewer's own permissions allow, so gating it again would be
 * redundant and would leave some users with nowhere to land.
 */
/** A module none of whose screens is built yet. */
export function isComingSoon(module: ModuleDef): boolean {
  return !module.screens.some((s) => s.built);
}

export function canSee(screen: ScreenDef, can: (permission: string) => boolean): boolean {
  if (screen.permission === '*' || can(screen.permission)) return true;
  return (screen.alsoVisibleWith ?? []).some((p) => can(p));
}

/** The first screen the user is allowed to see, used as their landing page. */
export function firstAllowedScreen(can: (permission: string) => boolean): string {
  for (const m of MODULES) {
    for (const s of m.screens) {
      if (s.built && canSee(s, can)) return s.id;
    }
  }
  return 'home';
}
